import React, { useEffect } from "react";
import { FilterDialog } from "../FilterDialog/FilterDialog";
import { FilterButton } from "../FilterButton/FilterButton";
import { useFeatureFlagValue } from "../../config/FeatureFlagManager/hooks.ts";
import { useInsightsAgentService } from "../../adapters/insights-agent/state/hooks/useInsightsAgentService.tsx";
import { useFullFilterOptionsState } from "../../adapters/insights-agent/state/hooks/stateHooks.ts";
import {
  useFiltersLoadingState,
  useFiltersState,
} from "../../adapters/insights-agent/state/hooks/stateHooks.ts";
import { ResearchId } from "../../adapters/insights-agent/state/AppState.ts";

export interface FilterOptionsState {
  /**
   * Legacy filter options format
   * @deprecated on August 2025
   */
  items?: Record<string, { value: string[] }>;
  /**
   * New filter options format
   * valid from August 2025
   */
  list?: {
    label: string;
    key: string;
    type: "selection_multiple" | "month_range";
    custom: boolean;
    options?: {
      label: string;
      value: string;
    }[];
  }[];
  loading: boolean;
}

export interface ConnectedFilterDialogProps {
  filterState: Record<string, unknown>;
  loading: boolean;
  filterOptionsState: FilterOptionsState;
  enableDynamicFilters: boolean;
  disabled?: boolean;
  onApply: (filters: Record<string, unknown>) => void;
  onClear: () => void;
}

export const ConnectedFilterDialog: React.FC<{
  researchId: ResearchId;
  disabled?: boolean;
}> = (props) => {
  const [open, setOpen] = React.useState(false);
  const appService = useInsightsAgentService();
  const filterState = useFiltersState(props.researchId);
  const loading = useFiltersLoadingState();
  const filterOptionsState = useFullFilterOptionsState();

  useEffect(() => {
    appService
      .refreshFilters({
        researchId: props.researchId,
      })
      .then();
    appService
      .refreshFilterOptions({
        researchId: props.researchId,
      })
      .then();
  }, [appService, props.researchId]);

  const enableDynamicFilters = useFeatureFlagValue("enableDynamicFilters");

  return (
    <>
      <FilterButton onClick={() => setOpen(true)} disabled={props.disabled} />
      {open && (
        <FilterDialog
          onClose={() => setOpen(false)}
          legacyFilterOptions={filterOptionsState.items}
          newFilterOptions={
            enableDynamicFilters ? filterOptionsState.list : undefined
          }
          filterState={filterState}
          loading={loading}
          onApply={(filters) => {
            appService
              .setFilters({
                researchId: props.researchId,
                filters,
              })
              .then(() => {
                setOpen(false);
              });
          }}
          onClear={() => {
            appService
              .setFilters({
                researchId: props.researchId,
                filters: {},
              })
              .then(() => {
                setOpen(false);
              });
          }}
        />
      )}
    </>
  );
};
